import { useState, useMemo, ChangeEvent, useEffect, useRef } from "react";
import { useScrollRestoration } from "../hooks/useScrollRestoration";
import styled from "styled-components";
import { useTranslation } from "@coworker/locales";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useMyStore } from "../../../core/auth/useLoggedInUser";
import { useNotificationsByStore } from "../hooks/useNotificationsByStore";
import { useMediasByStoreId } from "../hooks/useMediasByStoreId";
import { MediasViewSearchListDesktop } from "../Organisms/MediasViewSearchListDesktop";
import { MessageAndButton } from "../Molecules/MessageAndButton";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import { Media, MediasResponse } from "../types/media";
import { SignedUrlRelation } from "../types/images";
import { MessageModal } from "../Organisms/Modals/MessageModal";
import { useUpdateMediaWithId } from "../hooks/useUpdateMedia";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { MediasViewListDesktop } from "../Organisms/MediasViewListDesktop";
import { useSearchArticlesInMedias } from "../hooks/useSearchArticlesInMedias";
import { useMatchingMedias } from "../../../hooks/useMatchingMedias";
import { MediasViewTabs } from "../Molecules/MediasViewTabs";
import { useFilteredMedias } from "../hooks/useFilteredMedias";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import { useMediaOverviewFilters } from "../hooks/useMediaOverviewFilters";
import { mapUniqueMediaAreas } from "../Helpers/mapUniqueMediaAreas";
import { NoResults } from "../Molecules/NoResults";
import { useTaskLinks } from "../hooks/useTasksByTaskLinks";
import { TaskListingTypes } from "../constants";
import { useSignedUrls } from "../hooks/useSignedUrls";
import { AskFixa } from "../AskFixaChat/AskFixa";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { usePersistedMediaTypeTab } from "../hooks/usePersistedMediaTypeTab";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { MEDIA_TASK_TYPE_NAMES } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { useMediaTaskCountsV2 } from "@coworker/apprestructured/src/media-maintenance/hooks/useMediaTaskCountsV2";

const ScrollContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 0px 64px 64px 64px;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 40px;
  padding-bottom: 40px;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
`;

const HeaderTitleText = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const TabsWrapper = styled.div`
  padding-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

const StyledFixaSearch = styled(FixaSearch)`
  flex-shrink: 0;
`;

const MediaMaintenanceMessage = styled(FixaInlineMessage)`
  margin-inline: 8px;
  margin-top: 8px;
`;

export function MediasViewDesktop() {
  const [showModalMessage, setShowModalMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [displayedQuery, setDisplayedQuery] = useState("");
  const enableMediaAssistant = useFlag(FLAGS.ENABLE_MEDIA_AI_ASSISTANT);
  const { selectedMediaTypeTab, setSelectedMediaTypeTab } =
    usePersistedMediaTypeTab();
  const tabsRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const navigate = useFixaNavigate();
  const myStore = useMyStore();
  const {
    data: mediasResponse,
    isError,
    isLoading: isMediasLoading,
  } = useMediasByStoreId(myStore.storeId);

  const mainImageFileNames = useMemo(
    () =>
      (mediasResponse?.medias
        ?.map((m) => m.mainImageFileName)
        .filter(Boolean) as string[]) ?? [],
    [mediasResponse?.medias]
  );

  const { data: signedUrlRelationsResponse, isLoading: isLoadingSignedUrls } =
    useSignedUrls(mainImageFileNames, "large");

  const signedUrlRelations: SignedUrlRelation[] =
    isLoadingSignedUrls || !signedUrlRelationsResponse
      ? []
      : signedUrlRelationsResponse;

  const mediasWithResolvedMainImageUrl = useMemo(() => {
    return (mediasResponse?.medias ?? []).map((media) => {
      if (!media.mainImageFileName) return { ...media, mainImageUrl: "" };
      const relation = signedUrlRelations.find((r) =>
        media.mainImageFileName!.includes(r.fileName)
      );
      if (!relation) return { ...media, mainImageUrl: "" };
      return { ...media, mainImageUrl: relation.signedUrl };
    });
  }, [mediasResponse?.medias, signedUrlRelations]);

  const { data: notifications } = useNotificationsByStore(
    myStore.storeId,
    myStore.countryId
  );

  const updateMediaMutation = useUpdateMediaWithId();

  const uniqueMediaAreas = mapUniqueMediaAreas(mediasResponse?.medias ?? []);
  const mediaOverviewFilters = useMediaOverviewFilters(
    uniqueMediaAreas,
    notifications?.uniqueEdsDates ?? []
  );
  const { data: articles } = useSearchArticlesInMedias(
    myStore.storeId,
    searchQuery,
    mediaOverviewFilters.filterValues.find((f) => f.filterId === "articleHfb")
      ?.value as string | undefined
  );
  const tasks = useTaskLinks(
    notifications?.taskLinks || [],
    TaskListingTypes.Ongoing
  );

  // Fetch v2 task counts for all medias when flag enabled
  const enableMediaV2Tasks = useFlag(FLAGS.ENABLE_MEDIA_V2_TASKS);
  const { data: taskTypes = [] } = useAllTaskTypes();

  const mediaTaskTypeIds = useMemo(
    () =>
      taskTypes
        .filter((tt) =>
          (MEDIA_TASK_TYPE_NAMES as readonly string[]).includes(tt.name)
        )
        .map((tt) => tt.id),
    [taskTypes]
  );

  const { data: v2TaskCountsMap = new Map() } = useMediaTaskCountsV2(
    enableMediaV2Tasks ? mediaTaskTypeIds : []
  );

  // Combine v1 and v2 task counts per media
  const taskCountsByMediaId = useMemo(() => {
    const map = new Map<string, number>();

    // Count v1 tasks from task links
    tasks.taskLinks.forEach((tl) => {
      map.set(tl.mediaId, (map.get(tl.mediaId) || 0) + 1);
    });

    // Add v2 task counts
    v2TaskCountsMap.forEach((v2Count, mediaId) => {
      map.set(mediaId, (map.get(mediaId) || 0) + v2Count);
    });

    return map;
  }, [tasks.taskLinks, v2TaskCountsMap]);

  const { data: articlesFromHfb, isLoading: isLoadingArticlesHfb } =
    useSearchArticlesInMedias(
      myStore.storeId,
      undefined,
      mediaOverviewFilters.filterValues.find((f) => f.filterId === "articleHfb")
        ?.value as string | undefined
    );

  const filteredMedias = useFilteredMedias(
    mediasWithResolvedMainImageUrl,
    notifications?.alerts,
    tasks,
    selectedMediaTypeTab,
    mediaOverviewFilters.filterValues,
    articlesFromHfb,
    v2TaskCountsMap
  );
  const matchingMedias = useMatchingMedias(filteredMedias, searchQuery);

  const scrollContainerRef = useScrollRestoration(
    "mediasViewDesktop-scroll",
    !isMediasLoading && !isLoadingArticlesHfb
  );

  useEffect(() => {
    if (isMediasLoading || isLoadingArticlesHfb) return;

    const activeTabButton = tabsRef.current?.querySelector<HTMLElement>(
      'button[aria-selected="true"]'
    );
    if (!activeTabButton) return;

    // Scroll only the tabs strip's internal horizontal scroll container so the
    // active tab is centred. We deliberately avoid scrollIntoView() because it
    // propagates to every ancestor scroll container (including the outer page
    // scroll container), which would reset the scroll position restored by
    // useScrollRestoration.
    let scrollEl: HTMLElement | null = activeTabButton.parentElement;
    while (scrollEl && scrollEl !== tabsRef.current) {
      const { overflowX } = window.getComputedStyle(scrollEl);
      if (overflowX === "auto" || overflowX === "scroll") break;
      scrollEl = scrollEl.parentElement;
    }

    if (!scrollEl || scrollEl === tabsRef.current) return;

    const containerRect = scrollEl.getBoundingClientRect();
    const buttonRect = activeTabButton.getBoundingClientRect();
    const targetLeft =
      scrollEl.scrollLeft +
      buttonRect.left -
      containerRect.left -
      (scrollEl.offsetWidth - buttonRect.width) / 2;

    scrollEl.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  }, [isMediasLoading, isLoadingArticlesHfb, selectedMediaTypeTab]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const sanitizedInput = inputValue.replace(/[.,-]/g, "");
    setDisplayedQuery(inputValue);
    setSearchQuery(sanitizedInput);
  };

  const onClear = () => {
    setDisplayedQuery("");
    setSearchQuery("");
  };

  const onSaveMedia = async (media: Media) => {
    try {
      const { nbrArticles: nbr_articles, pieces, ...saveMedia } = media;
      await updateMediaMutation.mutateAsync(saveMedia);
    } catch (error) {
      console.log(error);
      setShowModalMessage(t("couldNotUpdateRoomTryAgainString"));
    }
  };

  // No medias
  if (mediasResponse?.medias && mediasResponse.medias.length === 0) {
    return (
      <>
        <MessageAndButton
          keyForMessageText="noMediasCreatedString"
          keyForButtonText="addMediaString"
          buttonAction={async () => {
            navigate(`/mediamaintenance/create`);
          }}
        />
      </>
    );
  }

  if (isError) {
    return (
      <MessageAndButton
        keyForMessageText="couldNotLoadMediasString"
        keyForButtonText="tryAgainString"
        buttonAction={async () => {
          window.location.reload();
        }}
      />
    );
  }

  const doSearch = searchQuery.length > 1;

  return (
    <>
      <ScrollContainer ref={scrollContainerRef}>
        <ToolbarContainer>
          <HeaderTitleText>{t("mediaMaintenanceString")}</HeaderTitleText>
          <ToolbarActions>
            <StyledFixaSearch
              id="media-search"
              value={displayedQuery}
              onChange={onChange}
              onClear={onClear}
              placeholder={t("mediasViewSearch")}
              ariaLabel={t("mediasViewSearch")}
              style={{ width: "310px" }}
            />
            <FixaButton
              aria-label={t("addMediaString")}
              text={t("addMediaString")}
              type="primary"
              data-testid="addRoomSettingsButton"
              onClick={() => navigate(`/mediamaintenance/create`)}
            />
          </ToolbarActions>
        </ToolbarContainer>

        <MediaMaintenanceMessage
          title={t("mediaMaintenanceBannerTitleString")}
          body={t("mediaMaintenanceBannerBodyString").trim()}
          variant="informative"
          ssrIcon={InformationCircleIconPath}
        />

        <FilterWrapper>
          <div>
            <FilterPanel
              filterValues={mediaOverviewFilters.filterValues}
              setFilterValues={mediaOverviewFilters.setFilterValues}
              useFiltersFactory={mediaOverviewFilters.useFiltersFactory}
              feature="media"
            />
          </div>
        </FilterWrapper>
        {enableMediaAssistant && <AskFixa />}

        <TabsWrapper ref={tabsRef}>
          <MediasViewTabs
            onTabChange={setSelectedMediaTypeTab}
            activeTab={selectedMediaTypeTab}
            allMedias={mediasWithResolvedMainImageUrl}
            filterValues={mediaOverviewFilters.filterValues}
            query={searchQuery}
            alerts={notifications?.alerts ?? []}
            tasks={tasks}
            loading={isMediasLoading || isLoadingArticlesHfb}
            articlesFromHfb={articlesFromHfb}
          />
        </TabsWrapper>

        {doSearch ? (
          <MediasViewSearchListDesktop
            mediasResponse={
              {
                medias: matchingMedias,
                children: mediasResponse?.children,
              } as MediasResponse
            }
            alerts={notifications?.alerts}
            taskCountsByMediaId={taskCountsByMediaId}
            taskLinks={tasks.taskLinks}
            onSaveMedia={onSaveMedia}
            storeId={myStore.storeId}
            articles={articles ?? undefined}
          />
        ) : (
          <MediasViewListDesktop
            mediasResponse={
              {
                medias: filteredMedias,
                children: mediasResponse?.children,
              } as MediasResponse
            }
            alerts={notifications?.alerts}
            taskCountsByMediaId={taskCountsByMediaId}
            onSaveMedia={onSaveMedia}
            loading={isMediasLoading || isLoadingArticlesHfb}
            filterValues={mediaOverviewFilters.filterValues}
          />
        )}
        <NoResults
          isSearch={searchQuery.length > 0}
          isFilters={mediaOverviewFilters.filterValues.length > 0}
          mediasCount={filteredMedias?.length ?? 0}
          matchingMediasCount={matchingMedias?.length ?? 0}
          articlesCount={articles?.length ?? 0}
          mediaType={selectedMediaTypeTab}
          query={searchQuery}
          clearFilters={() => mediaOverviewFilters.setFilterValues([])}
          clearSearch={onClear}
          setTabToAllMedias={() => setSelectedMediaTypeTab("allMedia")}
        />
      </ScrollContainer>

      {!!showModalMessage && (
        <MessageModal
          message={showModalMessage}
          closeModal={() => setShowModalMessage("")}
        />
      )}
    </>
  );
}
